import { useState, useEffect, useRef, useCallback } from 'react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function useAutoSave<T>(
  value: T,
  saveFn: (value: T) => Promise<void>,
  delay: number = 1000,
) {
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [saveError, setSaveError] = useState<Error | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveFnRef = useRef(saveFn);
  const initialRender = useRef(true);
  const lastSavedRef = useRef<string>(JSON.stringify(value));
  // Track the latest value so the unmount cleanup can flush whatever was
  // typed last, even if the debounce window hasn't elapsed yet.
  const valueRef = useRef(value);

  saveFnRef.current = saveFn;
  valueRef.current = value;

  // Status-bearing save used by the debounce timer and saveNow. Surfaces
  // failures via status/saveError instead of silently swallowing them.
  const performSave = useCallback(async (val: T) => {
    const serialized = JSON.stringify(val);
    setStatus('saving');
    setSaveError(null);
    try {
      await saveFnRef.current(val);
      lastSavedRef.current = serialized;
      setStatus('saved');
    } catch (err) {
      setSaveError(err instanceof Error ? err : new Error(String(err)));
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    // Skip the initial render
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    const serialized = JSON.stringify(value);
    if (serialized === lastSavedRef.current) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      void performSave(value);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [value, delay, performSave]);

  // Flush any pending edit on unmount so navigating away mid-debounce does
  // not silently drop the last keystroke. Fire-and-forget: the component is
  // gone, so we dispatch the save without touching React state.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      const serialized = JSON.stringify(valueRef.current);
      if (serialized !== lastSavedRef.current) {
        lastSavedRef.current = serialized;
        void Promise.resolve(saveFnRef.current(valueRef.current)).catch(
          () => {
            // Component is unmounting; nothing left to surface the error to.
          },
        );
      }
    };
  }, []);

  const saveNow = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    await performSave(valueRef.current);
  }, [performSave]);

  return {
    // `saving` retained for backward compatibility with existing consumers.
    saving: status === 'saving',
    status,
    saveError,
    saveNow,
  };
}
